import React, { memo, useCallback } from 'react';
import Toolbar from '@mui/material/Toolbar';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import MuiAppBar from '@mui/material/AppBar';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Avatar from '@mui/material/Avatar';
import PopoverButton from 'components/Popover/PopoverButton';
import { useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import Link from 'studio/components/Link';
import { useLoader } from 'components/loaders/FadeLoader';
import { useMessage } from 'studio/components/Message';
import services from 'services';
import Tooltip from '@mui/material/Tooltip';
import { veezooUrls } from 'config/constants';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { SlackIcon, VeezooIcon } from 'components/Icons';
import { trackEvent } from 'utils/eventTracking';
import Menu from 'studio/components/Menu/Menu';
import SvgIcon from '@mui/material/SvgIcon';
import VeezooLogoWhite from 'svg/veezoo-logo-white.svg';
import sx from './AppBar.styles';

const getInitials = name => {
  const initials = name.match(/\b\w/g) || [];
  return ((initials.shift() || '') + (initials.pop() || '')).toUpperCase();
};

const AppBar = ({
  title,
  icon: Icon,
  href,
  isDarkTheme,
  toggleTheme,
  isSingleSignOn,
  knowledgeGraph,
  user,
  additionalButton,
  children
}) => {
  const { withLoader } = useLoader();
  const { errorMessage } = useMessage();
  const { showConfirmationModal } = useConfirmationModal();

  const signOut = useCallback(async () => {
    const response = await withLoader(services.signout());
    if (response.success) {
      window.open('login', '_self');
    } else {
      const message = response?.data?.data?.message || 'There was an error while signing out.';
      errorMessage(message);
    }
  }, [withLoader, errorMessage]);

  const button = (label, icon, href, tooltip, onClick, knowledgeGraph) => (
    <Tooltip title={tooltip} placement="bottom">
      <Button
        variant="contained"
        color="primary"
        disableElevation
        sx={sx.button}
        component={Link}
        href={href}
        knowledgeGraph={knowledgeGraph}
        newWindow
        onClick={onClick}
      >
        {icon}
        {label}
      </Button>
    </Tooltip>
  );

  return (
    <MuiAppBar position="fixed" sx={sx.appBar} enableColorOnDark>
      <Toolbar variant="dense">
        <Grid container justifyContent="space-between">
          <Grid item>
            <Box sx={sx.drawer}>
              <Link href={href} data-test="appBarLogo">
                <Typography variant="h6" color="inherit" sx={sx.title}>
                  <Icon sx={sx.icon} />
                  <SvgIcon component={VeezooLogoWhite} viewBox={VeezooLogoWhite().props.viewBox} sx={sx.logo} />
                  <span>{title.toLowerCase()}</span>
                </Typography>
              </Link>
            </Box>

            {button(
              'Open Veezoo',
              <VeezooIcon />,
              '/chat',
              'Open Veezoo Chat to ask questions',
              undefined,
              knowledgeGraph
            )}

            {!!additionalButton &&
              button(additionalButton.title, additionalButton.icon, additionalButton.href, additionalButton.title)}

            {button('Open Docs', <MenuBookIcon />, veezooUrls.docs, 'Open the documentation', () =>
              trackEvent('Docs Opened')
            )}

            {button('Join Slack', <SlackIcon />, veezooUrls.slack, 'Join our Slack community', () =>
              trackEvent('Veezoo Slack Opened')
            )}
          </Grid>

          <Grid item sx={sx.buttons}>
            {children}

            <PopoverButton
              button={
                <Tooltip title="Open preferences menu" placement="bottom">
                  <IconButton color="inherit" size="large" data-test="preferencesMenuButton">
                    <AccountCircleIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <Card sx={sx.card}>
                <CardHeader
                  avatar={<Avatar>{getInitials(user.username)}</Avatar>}
                  title={user.username}
                  subheader={<span title={user.email}>{user.email}</span>}
                />
                <CardContent>
                  <Menu
                    items={[
                      isDarkTheme
                        ? { title: 'Switch to light mode', icon: Brightness7Icon, onClick: toggleTheme }
                        : { title: 'Switch to dark mode', icon: Brightness4Icon, onClick: toggleTheme },
                      !isSingleSignOn
                        ? [
                            {
                              title: 'Sign out',
                              icon: ExitToAppIcon,
                              onClick: () =>
                                showConfirmationModal(() => ({
                                  title: 'Sign out',
                                  content: `Are you sure that you want to sign out?`,
                                  onConfirm: signOut
                                }))
                            }
                          ]
                        : []
                    ].flat()}
                  />
                </CardContent>
              </Card>
            </PopoverButton>
          </Grid>
        </Grid>
      </Toolbar>
    </MuiAppBar>
  );
};

export default memo(AppBar);
